import axios from 'axios'

import InterWebviewBridge, {
  interWbNavigate,
  IWbHttpData,
  interWbHttp,
  interWbSession,
  WbEnvironments,
  IWbISafeResponse,
  interWbAuth,
  IWbAppInfo,
  IWbTic,
  IWbTac,
  IWbUserInfo,
} from '@interco/inter-webview-bridge'

import { HttpMethod, TParamsRequest, TResponseType } from './types'

export default class {
  static goBack = async () => {
    await interWbNavigate.requestGoBack()
  }

  static sendAnalyticsEvent = ({
    name,
    params,
  }: {
    name: string
    params?: Record<string, string>
  }): Promise<unknown> | null => interWbSession.requestAnalytics(name, params)

  static formatResponse = (response: unknown) =>
    typeof response === 'string' && response[0] === '{' ? JSON.parse(response || '[]') : response

  static doRequest = async <T>({
    headers,
    pathname = '',
    method,
    data,
    host,
    timeout,
  }: TParamsRequest): Promise<TResponseType<T>> => {
    const requestHeader = {
      ...headers,
      'Content-Type': 'application/json',
    }

    const url = `/mobile${pathname}`
    const payload = typeof data === 'string' ? JSON.parse(data) : data
    let bridgeResponse = {} as IWbHttpData

    if (InterWebviewBridge.getInstance().getEnvironment() === WbEnvironments.BROWSER) {
      const finalHeaders: Record<string, string> = { ...requestHeader }
      if (process.env.REACT_APP_ENABLE_MSW === 'false' && process.env.REACT_APP_AUTH_TOKEN) {
        finalHeaders.Authorization = `Bearer ${process.env.REACT_APP_AUTH_TOKEN}`
      }
      return axios({
        headers: finalHeaders,
        url: `${host}${pathname}`,
        method,
        data,
        timeout,
      })
    }
    switch (method) {
      case HttpMethod.GET: {
        bridgeResponse = await interWbHttp.get(url, requestHeader, {
          timeout,
        })
        break
      }
      case HttpMethod.POST: {
        bridgeResponse = await interWbHttp.post(url, payload, requestHeader, {
          timeout,
        })
        break
      }
      case HttpMethod.PUT: {
        bridgeResponse = await interWbHttp.put(url, payload, requestHeader, {
          timeout,
        })
        break
      }
      case HttpMethod.DELETE: {
        bridgeResponse = await interWbHttp.delete(url, requestHeader, {
          timeout,
        })
        break
      }
      default:
        break
    }

    const { httpStatus = 0, headers: responseHeaders = {} } = bridgeResponse

    let parsedData = {} as T
    if (typeof bridgeResponse.response === 'string') {
      parsedData = JSON.parse(bridgeResponse.response || '{}')
    }
    return {
      data: parsedData,
      status: httpStatus,
      headers: responseHeaders,
    }
  }

  static openNativeBrowser = (url = ''): Promise<unknown> => interWbNavigate.openNativeBrowser(url)

  static isAndroid = () => /(Android)/.test(navigator.userAgent)

  static getUserInfo = async (): Promise<IWbUserInfo | undefined> => {
    try {
      // If running in browser (development), return mock user data
      if (
        InterWebviewBridge.getInstance().getEnvironment() === WbEnvironments.BROWSER &&
        process.env.REACT_APP_ENV === 'development'
      ) {
        // Mock user birthDate - change age here to test different scenarios:
        // - 25 (adult, allows access)
        // - 17 (minor, shows restriction screen)
        // 1580007600000 is 1990-12-15T03:00:00.000Z
        const mockBirthDate = new Date()
        mockBirthDate.setFullYear(mockBirthDate.getFullYear() - 18)

        return {
          cpf: process.env.REACT_APP_CPF || '08791856000',
          account: process.env.REACT_APP_CONTA || '174725116',
          name: 'Usuário Teste',
          email: 'teste@teste.com',
          birthDate: mockBirthDate.getTime(),
        } as IWbUserInfo
      }
      return await interWbSession.getUserInfo()
    } catch (e) {
      return undefined
    }
  }

  static updateToolbar = async (title: string, show = false) => {
    try {
      return await interWbSession.editToolbar(title, show)
    } catch (e) {
      return e
    }
  }

  static async isafeFGTS(value: number): Promise<IWbISafeResponse | undefined> {
    const CATEGORY = '75'
    const CHECK_TYPE = '5'
    const FEATURE = 'AUTORIZACAO_CREDITO_PESSOAL'

    return interWbAuth.requestISafe(CATEGORY, CHECK_TYPE, FEATURE, value)
  }

  static getEnvironment = () => InterWebviewBridge.getInstance().getEnvironment()

  static async openPdf(url: string, title: string): Promise<void> {
    try {
      await interWbNavigate.openPdf(title, url)
    } catch (e) {
      // TODO: Tratar erro
      console.error(e)
    }
  }

  static async getAppInfo(): Promise<IWbAppInfo | undefined> {
    try {
      return interWbSession.getAppInfo()
    } catch (e) {
      return undefined
    }
  }

  static async isWeb(): Promise<boolean> {
    return InterWebviewBridge.getInstance().getEnvironment() === WbEnvironments.BROWSER
  }

  static async tic(
    params: Pick<IWbTic, 'action' | 'name'> & Partial<Pick<IWbTic, 'params'>>,
  ): Promise<IWbTic | undefined> {
    if (await this.isWeb()) return undefined
    return interWbSession.tic({
      action: params.action,
      module: 'antecipacao_fgts_v5',
      name: params.name,
      params: params.params ?? {},
    })
  }

  static async tac(
    params: Pick<IWbTac, 'actions' | 'name' | 'isError'> & Partial<Pick<IWbTac, 'params'>>,
  ): Promise<IWbTac | undefined> {
    if (await this.isWeb()) return undefined
    return interWbSession.tac({
      actions: params.actions,
      module: 'antecipacao_fgts_v5',
      name: params.name,
      params: params.params ?? {},
      isError: params.isError,
    })
  }
}
